import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useBrandingQuery } from "../apollo/branding";
import { useAuth } from "../auth/AuthProvider";

export type Branding = {
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  backgroundColor: string | null;
  tertiaryColor: string | null;
  primaryBgColor: string | null;
  secondaryBgColor: string | null;
  tertiaryBgColor: string | null;
  headingFont: string | null;
  bodyFont: string | null;
};

type BrandingContextValue = {
  branding: Branding | null;
  loading: boolean;
  error: Error | null | undefined;
  refresh: () => void;
};

const BrandingContext = createContext<BrandingContextValue | undefined>(
  undefined,
);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { data, loading, error, refetch } = useBrandingQuery();
  const { user } = useAuth();
  const effectiveTenantId =
    user?.impersonating?.tenant_id ?? user?.tenant_id ?? null;
  const [branding, setBranding] = useState<Branding | null>(null);

  // Reflect the latest fetched branding. We derive straight from `data` rather
  // than nulling on tenant change and waiting for a separate effect to re-apply
  // it: the auth check resolves asynchronously, so the effective tenant can flip
  // (null -> "legalhd") *after* the branding query has already resolved. A
  // reset-to-null-on-tenant-change effect would then blank the branding and
  // never re-apply (the `data` reference hasn't changed), leaving the sidebar
  // stuck on its text fallback. Cross-tenant safety lives at the cache layer —
  // login resetStore() refetches and logout clearStore() empties the cache — so
  // `data` already matches the current tenant.
  useEffect(() => {
    setBranding(data?.branding ?? null);
  }, [data]);

  // The branding query takes no variables, so it won't auto-refetch when the
  // effective tenant changes (e.g. starting/stopping impersonation). Nudge it
  // so branding follows the active tenant.
  useEffect(() => {
    void refetch();
  }, [effectiveTenantId, refetch]);

  const refresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  return (
    <BrandingContext.Provider
      value={{ branding, loading, error, refresh }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error("useBranding must be used within BrandingProvider");
  }
  return ctx;
}
